import {
  BrandBlogContentFormSubmission,
  IBrandBlogContentSubmission,
  InitiateCheckoutAllCarts,
  InitiateCheckoutBrandCart,
  InitiateCheckoutFlashSale,
  MakerAdPageApply,
  MakerSubmitApplication,
} from "@faire/retailer-visitor-shared/events";
import { Facebook } from "@faire/retailer-visitor-shared/lib/facebook";
import { loadScript } from "@faire/retailer-visitor-shared/lib/loadScript";
import { stringOrUndefined } from "@faire/retailer-visitor-shared/lib/stringOrUndefined";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { FacebookPixelEvents } from "@faire/retailer-visitor-shared/services/tracking/facebookPixelEvents";
import { getSettingFacebookRetailerPixelId } from "@faire/retailer-visitor-shared/settings/getSettingFacebookRetailerPixelId";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { logError } from "@faire/web/common/logging";
import { parseQueryParams } from "@faire/web/common/query-params/parseQueryParams";
import { isE2E } from "@faire/web/common/user-agent/isE2E";
import { trackBlogGatedContentBrandblogcontentformsubmission } from "@faire/web-api--source/events/blogGatedContent/actionCategoryOther/brandblogcontentformsubmission";
import { trackBlogGatedContentBrandblogformsubmissionover11Stockists } from "@faire/web-api--source/events/blogGatedContent/actionCategoryOther/brandblogformsubmissionover11Stockists";
import { trackBlogGatedContentBrandblogformsubmissionover25Stockists } from "@faire/web-api--source/events/blogGatedContent/actionCategoryOther/brandblogformsubmissionover25Stockists";
import { trackBrandAcquisitionBrandinboundapplication } from "@faire/web-api--source/events/brandAcquisition/actionCategoryOther/brandinboundapplication";
import { trackBrandAcquisitionMakersignupwithover11Stockists } from "@faire/web-api--source/events/brandAcquisition/actionCategoryOther/makersignupwithover11Stockists";
import { trackBrandAcquisitionMakersignupwithover25Stockists } from "@faire/web-api--source/events/brandAcquisition/actionCategoryOther/makersignupwithover25Stockists";
import { trackCheckoutRetailerInitiateCheckoutClick } from "@faire/web-api--source/events/checkout/click/retailerInitiateCheckout";
import { trackRetailerPosRetailerAddPaymentInfoClick } from "@faire/web-api--source/events/retailerPos/click/retailerAddPaymentInfo";
import { IBrandLead } from "@faire/web-api--source/indigofair/data/IBrandLead";
import { IPixelCustomDataParameters } from "@faire/web-api--source/indigofair/data/IPixelCustomDataParameters";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";

export const initializeFacebook = () => {
  if (!getSettingFacebookRetailerPixelId() || isE2E()) {
    // Don't initialize the facebook pixel script or fire any events
    return;
  }
  // TODO: Remove migrate to `useRetailer` once migrated to hooks
  const retailerToken = getRetailer()?.token;

  FacebookPixelEvents.get();

  const script = getDocumentOrThrow().getElementById("_fbevents");
  if (!script) {
    loadScript("https://connect.facebook.net/en_US/fbevents.js", "_fbevents");
  }

  InitiateCheckoutBrandCart.subscribe((event) => {
    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      cart_value: event.cartValue,
      currency: "USD",
      retailer_token: retailerToken ?? "",
      twitter_click_id: getTwitterClickId(),
    });
    trackCheckoutRetailerInitiateCheckoutClick(
      "USD",
      event.cartValue.toString(),
      retailerToken ?? "",
      pixelCustomDataParams
    );
  });

  InitiateCheckoutAllCarts.subscribe((event) => {
    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      cart_value: event.cartValue,
      currency: "USD",
      retailer_token: retailerToken ?? "",
      twitter_click_id: getTwitterClickId(),
    });
    trackCheckoutRetailerInitiateCheckoutClick(
      "USD",
      event.cartValue.toString(),
      retailerToken ?? "",
      pixelCustomDataParams
    );
  });

  InitiateCheckoutFlashSale.subscribe((event) => {
    const pixelCustomDataParams = IPixelCustomDataParameters.build({
      cart_value: event.cartValue,
      currency: "USD",
      retailer_token: retailerToken ?? "",
      twitter_click_id: getTwitterClickId(),
    });
    trackCheckoutRetailerInitiateCheckoutClick(
      "USD",
      event.cartValue.toString(),
      retailerToken ?? "",
      pixelCustomDataParams
    );
  });

  const handleMakerApplication = (brandLead: IBrandLead) => {
    try {
      const pixelCustomDataParams = IPixelCustomDataParameters.build({
        brand_lead_token: brandLead.token,
        brand_token: brandLead.brand_token,
        twitter_click_id: getTwitterClickId(),
      });

      Facebook.sendMakerEvent(Facebook.STANDARD.CompleteRegistration, {
        brand_token: brandLead.brand_token,
        brand_lead_token: brandLead.token,
      });
      trackBrandAcquisitionBrandinboundapplication(pixelCustomDataParams);

      const numberOfStockists = brandLead.number_of_stockists ?? 0;

      const pixelCustomDataParamsForStockists =
        IPixelCustomDataParameters.build({
          brand_lead_token: brandLead.token,
          twitter_click_id: getTwitterClickId(),
        });

      if (numberOfStockists > 11) {
        Facebook.sendMakerCustomEvent(
          "ifMakerApplicationSubmittedGreaterThan11Stockists",
          { brand_lead: brandLead }
        );
        trackBrandAcquisitionMakersignupwithover11Stockists(
          pixelCustomDataParamsForStockists
        );
      }

      if (numberOfStockists > 25) {
        Facebook.sendMakerCustomEvent(
          "ifMakerApplicationSubmittedGreaterThan25Stockists",
          { brand_lead: brandLead }
        );
        trackBrandAcquisitionMakersignupwithover25Stockists(
          pixelCustomDataParamsForStockists
        );
      }
    } catch (e) {
      logError(e);
    }
  };

  const handleBrandBlogContentFormSubmission = (
    submission: IBrandBlogContentSubmission
  ) => {
    try {
      Facebook.sendMakerCustomEvent("BrandBlogContentFormSubmission");
      trackBlogGatedContentBrandblogcontentformsubmission();

      const numberOfStockists = submission.stockists ?? 0;
      if (numberOfStockists > 11) {
        Facebook.sendMakerCustomEvent(
          "ifBrandBlogContentFormSubmissionGreaterThan11Stockists"
        );
        trackBlogGatedContentBrandblogformsubmissionover11Stockists();
      }

      if (numberOfStockists > 25) {
        Facebook.sendMakerCustomEvent(
          "ifBrandBlogContentFormSubmissionGreaterThan25Stockists"
        );
        trackBlogGatedContentBrandblogformsubmissionover25Stockists();
      }
    } catch (e) {
      logError(e);
    }
  };

  MakerSubmitApplication.subscribe(handleMakerApplication);
  MakerAdPageApply.subscribe(handleMakerApplication);
  BrandBlogContentFormSubmission.subscribe(
    handleBrandBlogContentFormSubmission
  );
};

export function getTwitterClickId(): string | undefined {
  const search = getWindow()?.location.search;
  if (search === undefined) {
    return undefined;
  } else {
    return stringOrUndefined(parseQueryParams(search).get("twclid"));
  }
}

export function getIncomeRangeFromRetailer(
  retailer: IRetailer | undefined
): string | undefined {
  if (retailer) {
    return `${retailer.annual_sales_min_amount?.amount_cents} - ${retailer.annual_sales_max_amount?.amount_cents} ${retailer.currency}`;
  }
  return undefined;
}

export function trackAddPaymentMethodClickEvent(retailerToken: string) {
  const pixelCustomDataParams = IPixelCustomDataParameters.build({
    retailer_token: retailerToken ?? "",
    twitter_click_id: getTwitterClickId(),
  });
  trackRetailerPosRetailerAddPaymentInfoClick(
    retailerToken ?? "",
    pixelCustomDataParams
  );
}
